import { Component, Host, h, Prop, Element } from '@stencil/core';
import { addIntersectionObserver } from '../../utils/utils';

@Component({
  tag: 'banner-carousel',
  styleUrl: 'banner-carousel.css',
  shadow: true,
})
export class BannerCarousel {
  /**
   * Banner Slides Data
   */
  @Prop() bannerSlides: string;

  /**
   * Background Color
   */
  @Prop() backgroundColor: string;

  /**
   * Text Color
   */
  @Prop() textColor: string;

  /**
   * Padding Top
   */
  @Prop() paddingTop: string;

  /**
   * Padding Bottom
   */
  @Prop() paddingBottom: string;

  /**
   * Background Image Url
   */
  @Prop() backgroundImage: string;

  @Element() el: HTMLElement;

  private slideChangeInterval: any;
  private slideIndex: number = 0;

  componentDidLoad() {
    this.startCarousel();
    addIntersectionObserver(
      this.el.shadowRoot.querySelector('.home-banner-slide'),
      () => {
        this.el.shadowRoot.querySelector('.home-banner-slide').classList.add('animate');
      },
      ['.overlay1', '.overlay2'],
    );
  }

  disconnectedCallback() {
    this.stopCarousel();
  }

  startCarousel() {
    if (this.getNumberOfSlides() > 1) {
      this.slideChangeInterval = setInterval(() => {
        this.nextSlide();
      }, 7000); // Carousel Cycle Interval
    }
  }

  stopCarousel() {
    clearInterval(this.slideChangeInterval);
  }

  getNumberOfSlides() {
    const slides = JSON.parse(this.bannerSlides || '[]');
    return slides.length;
  }

  nextSlide() {
    const contentBlock = this.el.shadowRoot.querySelector('.home-banner-slide-content .block');
    const currentImage = this.el.shadowRoot.querySelector('.current-image');
    const nextImage = this.el.shadowRoot.querySelector('.next-image');
    const slideToggles = this.el.shadowRoot.querySelectorAll('.home-banner-slide-toggle');
    const totalSlides = slideToggles.length;

    if (!contentBlock || !currentImage || !nextImage || totalSlides === 0) {
      console.error('Required elements are missing, stopping carousel');
      this.stopCarousel();
      return;
    }

    // Start by fading out the current content block
    this.fadeOut(contentBlock, () => {
      // Once content fade out is complete, handle slide index and toggle updates
      slideToggles[this.slideIndex].classList.remove('active');
      this.slideIndex = (this.slideIndex + 1) % totalSlides;
      slideToggles[this.slideIndex].classList.add('active');

      // Determine the next slide index
      const nextImageUrl = slideToggles[this.slideIndex].getAttribute('data-imageurl');
      const nextImageAlt = slideToggles[this.slideIndex].getAttribute('data-imagealt');

      // Update content and prepare the next image
      this.updateSlideContent();

      // Fade in content and then initiate image transitions
      this.fadeIn(contentBlock);
      this.swapImages(currentImage, nextImage, nextImageUrl, nextImageAlt);
    });
  }

  swapImages(currentImage, nextImage, newSrc, newAlt) {
    // Set the next image's source and alternative text
    nextImage.src = newSrc;
    nextImage.alt = newAlt;

    // Start fading in the next image while it's still marked as the next image
    nextImage.classList.add('fading-in'); // Assume 'fading-in' handles opacity change
    nextImage.style.opacity = '1'; // Begin to fade in

    // Ensure this is the fade-in transition ending, not another transition
    setTimeout(() => {
      // Check if the next image has started to fade in
      if (nextImage.style.opacity === '1') {
        // Switch the roles of the images
        nextImage.classList.remove('next-image', 'fading-in');
        nextImage.classList.add('current-image');
        currentImage.classList.remove('current-image');
        currentImage.classList.add('next-image');
        currentImage.style.opacity = '0'; // Begin to fade out the old current image
      }
    }, 100); // Short delay
  }

  updateSlideContent() {
    const slideToggles = this.el.shadowRoot.querySelectorAll('.home-banner-slide-toggle');
    const activeSlide = slideToggles[this.slideIndex];

    // Extract data attributes from the active slide
    const title = activeSlide.getAttribute('data-title');
    const description = activeSlide.getAttribute('data-description');
    const buttonsJson = activeSlide.getAttribute('data-buttons');
    const buttons = buttonsJson ? JSON.parse(buttonsJson) : [];

    // Select elements within the component to update their content
    const slideContent = this.el.shadowRoot.querySelector('.home-banner-slide-content');
    if (slideContent) {
      slideContent.querySelector('.home-banner-slide-title').textContent = title || '';
      slideContent.querySelector('.home-banner-slide-description').innerHTML = description || '';

      // Correctly selecting the buttons container based on the actual class name used in your render method
      const buttonsContainer = this.el.shadowRoot.querySelector('.home-banner-slide-buttons');
      const existingButtonBlocks = buttonsContainer ? buttonsContainer.querySelectorAll('button-block') : [];

      // Dynamically updating, creating, or removing button-block elements based on the current slide's button data
      buttons.forEach((buttonData, index) => {
        if (index < existingButtonBlocks.length) {
          existingButtonBlocks[index].buttonsData = buttonData;
        } else {
          const newButtonBlock = document.createElement('button-block');
          newButtonBlock.buttonsData = buttonData;
          buttonsContainer.appendChild(newButtonBlock);
        }
      });

      // Remove excess button-blocks if there are more existing than needed
      if (existingButtonBlocks.length > buttons.length) {
        for (let i = buttons.length; i < existingButtonBlocks.length; i++) {
          buttonsContainer.removeChild(existingButtonBlocks[i]);
        }
      }

      // If there are no buttons for the current slide
      if (buttons.length === 0) {
        buttonsContainer.innerHTML = '';
      }
    }
  }

  displaySelectedSlide(selectedIndex) {
    // Stop the carousel
    this.stopCarousel();

    const contentBlock = this.el.shadowRoot.querySelector('.home-banner-slide-content .block');
    const assetWrapper = this.el.shadowRoot.querySelector('.home-banner-slide-asset-wrapper');
    const slideToggles = this.el.shadowRoot.querySelectorAll('.home-banner-slide-toggle');
    const currentImage = this.el.shadowRoot.querySelector('.home-banner-slide-asset.current-image');
    const nextImage = this.el.shadowRoot.querySelector('.home-banner-slide-asset.next-image');

    if (!contentBlock || !assetWrapper) {
      return; // Early exit if necessary elements are missing
    }

    // Start by fading out the current content and asset wrapper
    this.fadeOut(contentBlock, () => {
      // Once fade out is complete, update the active class for slides
      slideToggles.forEach((slide, idx) => {
        if (idx === selectedIndex) {
          slide.classList.add('active');
        } else {
          slide.classList.remove('active');
        }
      });

      // Extract data attributes from the selected slide
      const activeSlide = slideToggles[selectedIndex];
      const title = activeSlide.getAttribute('data-title');
      const description = activeSlide.getAttribute('data-description');
      const imageUrl = activeSlide.getAttribute('data-imageurl');
      const imageAlt = activeSlide.getAttribute('data-imagealt');
      const buttonsJson = activeSlide.getAttribute('data-buttons');
      const buttons = buttonsJson ? JSON.parse(buttonsJson) : [];

      // Update slide content
      const slideContent = this.el.shadowRoot.querySelector('.home-banner-slide-content');
      if (slideContent) {
        slideContent.querySelector('.home-banner-slide-title').textContent = title || '';
        slideContent.querySelector('.home-banner-slide-description').innerHTML = description || '';

        // Swap the images using the swapImages function
        this.swapImages(currentImage, nextImage, imageUrl, imageAlt);

        // Handle button blocks
        const buttonsContainer = this.el.shadowRoot.querySelector('.home-banner-slide-buttons');
        buttonsContainer.innerHTML = ''; // Clear existing button blocks
        buttons.forEach(buttonData => {
          const buttonBlock = document.createElement('button-block');
          buttonBlock.buttonsData = buttonData;
          buttonsContainer.appendChild(buttonBlock);
        });
      }

      // After content is updated, fade in both elements
      this.fadeIn(contentBlock);
    });
  }

  /**
   * wordCountTrim - Trims a string to a maximum length, attempting to avoid cutting words in half
   */
  wordCountTrim(str, maxLength) {
    if (str.length <= maxLength) return str;

    // Attempt to find the last space before maxLength to avoid cutting a word
    const lastSpaceIndex = str.slice(0, maxLength + 1).lastIndexOf(' ');

    // If a space is found, and it's not the very first character, trim up to this space
    if (lastSpaceIndex > 0) return str.slice(0, lastSpaceIndex) + '...';

    // If no suitable space is found, or the only space is at the very start, return the original method
    return str.slice(0, maxLength) + (str.length > maxLength ? '...' : '');
  }

  fadeIn(element) {
    element.classList.remove('animate-out');
    element.classList.add('animate-in');
  }

  fadeOut(element, onComplete = () => {}) {
    element.classList.add('animate-out');
    element.addEventListener(
      'animationend',
      () => {
        element.classList.remove('animate-in');
        onComplete();
      },
      { once: true },
    );
  }

  // private getText(): string {
  //   return format(this.first, this.middle, this.last);
  // }
  render() {
    const cards = JSON.parse(this.bannerSlides);
    //console.log('banner slides',cards);
    return (
      <Host>
        <div class={`container ${this.paddingBottom} ${this.paddingTop}`}>
          <div class={`home-banner ${this.backgroundColor} ${this.textColor}`} style={{ backgroundImage: `url(${this.backgroundImage})` }}>
            {cards.length > 0 && (
              <div key={0} class={`flex home-banner-slide home-banner-slide-0`}>
                <div class={`overlay1`}></div>
                <div class={`home-banner-slide-content flex flex-col ${cards.length == 1 ? 'centered-content' : ''}`}>
                  <div class={`block`}>
                    {cards[0].optional_date && <p>{cards[0].optional_date}</p>}
                    {cards[0].title && <h1 class={`home-banner-slide-title `}>{this.wordCountTrim(cards[0].title, 115)}</h1>}
                    {cards[0].description && <div class={`home-banner-slide-description`} innerHTML={cards[0].description}></div>}
                    {cards[0].buttons && cards[0].buttons.length > 0 && (
                      <div class="home-banner-slide-buttons flex flex-wrap gap-12px mt-32px">
                        {cards[0].buttons.map((button, buttonIndex) => (
                          <button-block key={buttonIndex} buttonsData={button}></button-block>
                        ))}
                      </div>
                    )}
                  </div>

                  <div id="home-banner-slide-items">
                    {/* Inner mapping to create divs with data attributes for all cards */}
                    {cards.length > 1 &&
                      cards.map(({ title, description, preview_text, optional_date, image_url, image_alt, buttons }, index) => (
                        <div
                          key={index}
                          class={`home-banner-slide-toggle ${index === 0 ? 'active' : ''}`}
                          data-title={this.wordCountTrim(title, 115)}
                          data-description={this.wordCountTrim(description, 175)}
                          data-date={optional_date}
                          data-imageurl={image_url}
                          data-imagealt={image_alt}
                          data-buttons={JSON.stringify(buttons)}
                          onClick={() => this.getNumberOfSlides() > 1 && this.displaySelectedSlide(index)}
                        >
                          <div class="home-banner-slide-preview-item">
                            <div class="home-banner-slide-preview-item-bar-wrapper">
                              <div class={`home-banner-slide-preview-item-bar bar-${index}`}></div>
                            </div>
                            <p>{preview_text}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                <div class="home-banner-slide-asset-wrapper">
                  {/* <div class={`overlay2`}></div> */}
                  {cards[0] && cards[0].image_url && (
                    // <image-block
                    //   imageClass="home-banner-slide-asset current-image"
                    //   imageUrl={cards[0].image_url}
                    //   imageAlt={cards[0].image_alt}
                    // />
                    <img class="home-banner-slide-asset current-image" src={cards[0].image_url} alt={cards[0].image_alt ? cards[0].image_alt : "banner image"} width={'900'} height={'800'} loading="eager" />
                  )}
                  {/* Placeholder for the next image, initially with no content and hidden */}
                  {cards[1] && cards[1].image_url && (
                    // <image-block
                    //   imageClass="home-banner-slide-asset next-image"
                    //   imageUrl={cards[1].image_url}
                    //   imageAlt={cards[1].image_url}
                    // />
                    <img class="home-banner-slide-asset next-image" src={cards[1].image_url} alt={cards[1].image_alt ? cards[1].image_alt : 'banner image'} width={'900'} height={'800'} loading="lazy" />
                  )}

                  {cards[0] && cards[0].image_url && (
                    // <image-block
                    //   imageClass="home-banner-slide-asset placeholder-image"
                    //   imageUrl={cards[0].image_url}
                    //   imageAlt={cards[0].image_alt}
                    // />

                    <img class="home-banner-slide-asset placeholder-image" src={cards[0].image_url} alt={cards[0].image_alt ? cards[0].image_alt : "banner image"} width={'900'} height={'800'} loading="lazy" />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
